import { IslandType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen } from '@rta/rta-blueprint-library';

export type GetIslandOverviewDescriptionParams = {
	island: Island;
	kitchen: Kitchen;
};

/**
 * Builds the per-island description line shown on the Kitchen Overview page,
 * matching the PDF blueprint format:
 *
 *   "8ft Grill Island | Base Length – 102" | Base Height – 34.5""
 *   "Corner Stand | Base Height – 37""
 *   "Asado Stand | Base Height – 34.5""
 *
 * Rules:
 *   - "Base Length" is included for Grill, Bar, and Smoker islands.
 *     Asado, Pizza, FirePit, and Corner islands omit Base Length.
 *   - "Island Height" from the library's getIslandDescriptionCfn is renamed
 *     to "Base Height" to match the PDF convention.
 */
const getIslandHeightTypePrefix = (island: Island): string => {
	const rawDescription = island.description ?? '';
	const firstSegment = rawDescription.split(' | ')[0]?.trim();
	if (firstSegment) {
		// Stands omit the size prefix in the overview ("Asado Stand", not "3ft Asado Stand"); grill/bar keep it.
		return island.type === IslandType.Grill || island.type === IslandType.Bar
			? firstSegment
			: firstSegment.replace(/^\d+(\.\d+)?ft\s+/i, '');
	}

	switch (island.type) {
		case IslandType.Corner: return 'Corner Stand';
		case IslandType.Asado: return 'Asado Stand';
		case IslandType.Pizza: return 'Pizza Stand';
		case IslandType.Smoker: return 'Smoker Stand';
		case IslandType.Bar: return 'Bar Island';
		case IslandType.FirePit: return 'Fire Pit';
		default: return 'Grill Island';
	}
};

const getBaseLength = (island: Island): number | null => {
	// Corner stands never quote Base Length on the overview line (0/42 finalized).
	if (island.type === IslandType.Corner) return null;
	return island.size.width ?? null;
};

const formatNumber = (value: number): string => {
	// Round to 4 decimal places so common inch fractions (1/8 = 0.125, 1/16 = 0.0625) render
	// exactly (34.625" must not be truncated to 34.63"). Trailing zeros still trim, so 34.5 → "34.5" and 36 → "36".
	const rounded = Math.round(value * 10000) / 10000;
	return rounded.toString();
};

export const getIslandOverviewDescription = (params: GetIslandOverviewDescriptionParams): string => {
	const { island } = params;
	const parts: Array<string> = [];

	parts.push(getIslandHeightTypePrefix(island));

	const baseLength = getBaseLength(island);
	if (baseLength !== null) {
		parts.push(`Base Length – ${formatNumber(baseLength)}"`);
	}

	const height = island.size.height;
	if (typeof height === 'number' && height > 0) {
		parts.push(`Base Height – ${formatNumber(height)}"`);
	}

	// Every island quotes its depth; corner islands store depth as {left,right} and are skipped by the guard.
	const depth = island.size.depth;
	if (typeof depth === 'number' && depth > 0) {
		parts.push(`Base Depth – ${formatNumber(depth)}"`);
	}

	return parts.join(' | ');
};
